import React, { useRef } from 'react'
import { Popover, PopoverAnchor, PopoverContent } from '../popover'
import { mergeClasses } from '../../../helpers/styling'
import { Command } from "cmdk"
import { CommandEmpty, CommandGroup, CommandItem, CommandList } from '../command'
import { Loader2Icon, Search, X, CircleAlert, TriangleAlert } from 'lucide-react'
import { DynamicIcon } from '../../DynamicIcon'
import { SearchOption, SearchOptionGroup, UNGROUPED } from './index'
import { useSearchDropdownController } from './controller'

export function SearchDropdownView() {
  const ctx = useSearchDropdownController()
  const props = ctx.props

  const labelErrorMessage = ctx.selectedLabelAsync.result.type === "error"
    ? ctx.selectedLabelAsync.result.error.message
    : null
  const isFetchingError = !!props.fetchingError

  return (
    <Popover open={ctx.open} onOpenChange={ctx.onOpen}>
      <Command className={ctx.props.classNames?.cmdkWrapper}
        shouldFilter={props.shouldFilter}
        filter={ctx.props.filterFn}
      >
        <PopoverAnchor asChild>
          {
            // First <div> must be here directly and not in component (<SearchInput>); else bugged:
            // https://github.com/radix-ui/primitives/issues/3293
          }
          <div
            id={props.id}
            role="combobox"
            aria-expanded={ctx.open}
            title={labelErrorMessage ? `Could not load a label for value: ${labelErrorMessage}` : undefined}
            className={mergeClasses(`
              w-full flex items-center gap-2 px-2
              border border-gray-200 rounded-lg bg-white text-black transition duration-150
              focus-within:ring-2`, {
              'px-2': ctx.variant === 'big',
              'px-2 text-sm': ctx.variant === 'default',
              "bg-yellow-100": !!labelErrorMessage
            }, ctx.props.classNames?.inputWrapper)}
            cmdk-input-wrapper="">
            <SearchInput />
          </div>
        </PopoverAnchor>
        <PopoverContent
          className={mergeClasses('p-0 w-max max-w-[100vw] overflow-hidden', {
            "bg-red-200": isFetchingError
            }, props.classNames?.popover)}
          style={{ zIndex: 9999, minWidth: 'var(--radix-popper-anchor-width)' }}
          onOpenAutoFocus={e => e.preventDefault()}
          onFocusOutside={workaroundCmdkEventIssue}
          onInteractOutside={workaroundCmdkEventIssue}>
          <OptionList />
        </PopoverContent>
      </Command>
    </Popover>
  )
}

function SearchInput() {
  const inputRef = useRef<HTMLInputElement>(null)
  const ctx = useSearchDropdownController()

  const isLabelError = ctx.selectedLabelAsync.result.type === "error"
  const isLoading = ctx.props.isLoading || ctx.selectedLabelAsync.result.type === "loading"

  const label = ctx.selectedLabelAsync.result.type === "success"
    ? ctx.selectedLabelAsync.result.value
    : "..."
  // Only need to put the label in placeholder if syncSearchWithLabel is off
  const placeholder = ctx.props.syncSearchWithLabel
    ? ctx.labels.placeholder
    : (ctx.props.value ? label : ctx.labels.placeholder)

  return (
    <>
      {isLabelError && <TriangleAlert className="py-1 text-red-800" />}

      <Command.Input
        ref={inputRef}
        value={ctx.search}
        onValueChange={ctx.onSearch}
        onFocus={ctx.openAndPropagate}
        onKeyDown={preventUnwantedCmdkKeydownBehaviour}
        placeholder={placeholder}
        className={mergeClasses(`
        w-full flex flex-grow h-10 py-3
        bg-transparent text-medium outline-none
        disabled:cursor-not-allowed disabled:opacity-50
        text-ellipsis placeholder:text-ellipsis`, {
          "placeholder:text-black": !!ctx.props.value,
          "py-3 px-2 h-10": ctx.variant === "big",
          "py-2 h-8": ctx.variant === "default",
        }, ctx.props.classNames?.input)} />

      {isLoading ? (
        <Loader2Icon
          role="status"
          aria-label="Loading"
          className="p-1 animate-spin text-wistor"
        />
      ) : (
        (ctx.search || ctx.props.value) ? (
          <button
            type="button"
            onClick={() => {
              ctx.onSearch('')
              ctx.props.onChange(null)
            }}
            className={`opacity-75 hover:opacity-100 hover:bg-gray-400 hover:bg-opacity-50 rounded-full mr-[-.25rem]`}
            aria-label="Clear">
            <X className="p-1" />
          </button>
        ) : (
          <Search className="p-1 opacity-50" onClick={() => {
            ctx.openAndPropagate()
            inputRef.current?.focus()
          }} />
        )
      )}
    </>
  )
}

function OptionList() {
  const ctx = useSearchDropdownController()

  const isFetchingError = Boolean(ctx.props.fetchingError)
  const hasNoOptions = ctx.derivedOptions.allOptions.length === 0
  const shouldShowLoading = !isFetchingError && ctx.props.isLoading
  const shouldShowEmpty = !isFetchingError && !ctx.props.isLoading && hasNoOptions && ctx.search.length > 0

  return (
    <CommandList className={mergeClasses(
      'flex flex-col [&>[cmdk-list-sizer]]:overflow-y-auto', {
        'text-sm': ctx.variant === "default",
        'text-medium': ctx.variant === "default",
      }, ctx.props.classNames?.list)}>
      {isFetchingError ? (
        <CommandGroup className="font-medium">
          <CommandEmpty className="w-full flex gap-2 items-center px-2 py-0.5 text-red-900">
            <CircleAlert className="py-1 text-red-800" />
            {ctx.labels.errorFetchingOptions}
          </CommandEmpty>
        </CommandGroup>
      ) : (
        <OptionGroups />
      )}

      {(shouldShowLoading || shouldShowEmpty) && (
        <CommandGroup className="font-medium opacity-75">
          {shouldShowLoading && (
            <Command.Loading className="">
              <div className="w-full flex gap-2 items-center px-2 py-0.5 select-none">
                <Loader2Icon
                  role="status"
                  aria-label="Loading"
                  className="size-3 animate-spin text-wistor"
                />
                {ctx.labels.loadingOptions}
              </div>
            </Command.Loading>
          )}
          {shouldShowEmpty && (
            <CommandEmpty className="w-full flex gap-2 items-center px-2 py-0.5 select-none">
              {ctx.labels.noOptionsFound}
            </CommandEmpty>
          )}
        </CommandGroup>
      )}
    </CommandList>
  )
}

function OptionGroups() {
  const ctx = useSearchDropdownController()

  const renderOptionLabel = ctx.props.renderOptionLabel ?? ((option: SearchOption) =>
    <DefaultOptionItemLabel option={option}
                            search={ctx.search}
                            highlightWords={ctx.props.highlightWords} />)

  const renderGroupHeading = ctx.props.renderGroupHeading ?? ((group: SearchOptionGroup) =>
    <DefaultGroupHeading group={group} />)

  return (
    <>
      {Array.from(ctx.derivedOptions.groupedOptions).map(([key, group]) => (
        <CommandGroup key={key === UNGROUPED ? null : key}
                      heading={key === UNGROUPED ? undefined : renderGroupHeading(group)}
                      className={ctx.props.classNames?.group}>
          {group.options.map(option => (
            <CommandItem
              key={option.value}
              value={option.value}
              onSelect={ctx.onSelect}
              keywords={[option.label]}
              className={mergeClasses('data-[selected=true]:bg-indigo-100 overflow-hidden text-ellipsis whitespace-nowrap', {
                "text-[16px]": ctx.variant === "big"
              }, ctx.props.classNames?.item)}>
              {renderOptionLabel(option)}
            </CommandItem>
          ))}
        </CommandGroup>
      ))}
    </>
  )
}

function DefaultOptionItemLabel(props: {
  option: SearchOption,
  search: string,
  highlightWords?: boolean
}) {
  return (
    <div className="flex gap-1.5 items-center">
      {props.option.icon && <DynamicIcon icon={props.option.icon} size="14px" />}
      {props.highlightWords
        ? <HighlightedLabel search={props.search} label={props.option.label} />
        : props.option.label
      }
    </div>
  )
}

function DefaultGroupHeading(props: {
  group: SearchOptionGroup
}) {
  if (!props.group.icon && !props.group.label) return undefined

  return (
    <div className="flex gap-2 items-center select-none">
      {props.group.icon && <DynamicIcon icon={props.group.icon} size="12px" className="mt-[1px]" />}
      {props.group.label}
    </div>
  )
}

export function HighlightedLabel(props: {
  label: string
  search: string
}) {
  const index = props.label.toLowerCase().indexOf(props.search.toLowerCase())
  if (index === -1) return props.label

  const markEnd = index + props.search.length
  const start = props.label.slice(0, index)
  const marked = props.label.slice(index, markEnd)
  const end = props.label.slice(markEnd)

  return (
    <span>
      <span>{start}</span>
      <mark className="p-0">{marked}</mark>
      <span>{end}</span>
    </span>
  )
}

// https://github.com/dip/cmdk/issues/300
function workaroundCmdkEventIssue(e: Event) {
  const isInCmdk = e.target instanceof HTMLElement && e.target.closest('[cmdk-input-wrapper]')
  if (!isInCmdk) return
  e.preventDefault()
}

// https://github.com/dip/cmdk/issues/341
function preventUnwantedCmdkKeydownBehaviour(event: React.KeyboardEvent) {
  switch (event.key) {
    case 'Home':
    case 'End':
      event.stopPropagation()
      break
  }
}
